/**
 * CodableList / CodableProjectsWidget — Lovable-like grid projektů tenanta.
 *
 * Widget-first (ModuleWorkspace + module-ui-catalog): NodePicker v panelu
 * přepíná cíl přes callWidgetTool → lokál MCP / mesh.rpc_call na peer.
 * Karty s gradient thumbnailem, status badge, hover akcemi.
 * "Nový projekt" → /modules/codable/new wizard.
 */
import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  AlertTriangle,
  Clock,
  Puzzle,
  Copy,
  Database,
  DownloadCloud,
  ExternalLink,
  GitCommit,
  Globe,
  Hash,
  Plus,
  Shield,
  Trash2,
} from 'lucide-react';
import { CodableIcon } from '../../../components/icons/CodableIcon';
import { operationPollInterval } from '../../../lib/bounded-polling';
import { mcpCall } from '../../../lib/mcp';
import { Badge, Button, EmptyState, Input, Label, Modal } from '../../../components/ui';
import { callWidgetTool } from '../../../widgets/scope';
import type { EffectiveScope, WidgetRenderProps } from '../../../widgets/types';
import AcmImportModal from './AcmImportModal';
import { visibleProjectId } from '../../../lib/project-id';
import { pickLiveOperation } from '../../../lib/lifecycle-operation';

interface CodableProject {
  id: string;
  orchestrator_project_id?: string | null;
  slug: string;
  name: string;
  description: string | null;
  frontend_domain: string | null;
  preview_url?: string | null;
  public_domain?: string | null;
  public_url?: string | null;
  public_desired?: boolean;
  public_actual?: boolean;
  frontend_public_enabled: boolean;
  frontend_public: boolean;
  created_at: string;
  updated_at?: string | null;
  last_commit_sha?: string | null;
  source?: 'native' | 'lovable' | 'acm';
  origin?: { node?: string; mode?: 'clone' | 'mirror'; stale?: boolean } | null;
  backend?: {
    kind?: string;
    type?: string;
    enabled?: boolean;
    runtime?: BackendRuntime | null;
  } | null;
  topology?: ProjectTopology | null;
  project_status?: ProjectStatus | null;
}

interface BackendRuntime {
  kind?: string | null;
  module?: string | null;
  status?: string | null;
  instance?: string | null;
  target_node?: string | null;
  locality?: 'local' | 'remote' | 'unknown' | string | null;
  local_url?: string | null;
  public_url?: string | null;
  studio_local_url?: string | null;
  studio_public_url?: string | null;
  port?: string | null;
  public_port?: string | null;
  same_instance?: boolean | null;
}

interface ProjectTopology {
  mode?: 'beta' | 'simple' | 'normal' | 'full_split' | string | null;
  label?: string | null;
  frontend_split?: boolean | null;
  backend_split?: boolean | null;
  verified?: boolean | null;
}

interface ProjectStatus {
  dev?: {
    short_head?: string | null;
    dirty?: boolean;
    ahead?: number | null;
    behind?: number | null;
  };
  prod?: {
    short_head?: string | null;
    dirty?: boolean;
    ahead?: number | null;
    behind?: number | null;
  };
  relation?: { states?: string[]; warnings?: string[] };
}

type TFn = TFunction;

// Badge zdroje projektu (CodAble=vývoj; přesun řeší orchestrátor=správa).
function sourceBadge(p: CodableProject, t: TFn) {
  const src = p.source ?? 'native';
  if (src === 'lovable') {
    return { variant: 'purple' as const, label: t('codable.sourceLovable', 'Lovable') };
  }
  if (src === 'acm') {
    const node = p.origin?.node ? `: ${p.origin.node}` : '';
    return { variant: 'blue' as const, label: `${t('codable.sourceAcm', 'ACM')}${node}` };
  }
  return { variant: 'gray' as const, label: t('codable.sourceNative', 'Native') };
}

// Badge backend stacku.
function backendBadge(
  p: CodableProject,
  t: TFn,
): { variant: 'green' | 'gray'; label: string } | null {
  const kind = p.backend?.kind;
  if (!kind || kind === 'none') {
    return { variant: 'gray', label: t('codable.backendNone', 'bez BE') };
  }
  if (kind === 'supabase') {
    return { variant: 'green', label: t('codable.backendSupabase', 'Supabase') };
  }
  if (kind === 'acm-modules') {
    return { variant: 'green', label: t('codable.backendAcmModules', 'ACM moduly') };
  }
  return { variant: 'green', label: kind };
}

interface ListResp {
  ok: boolean;
  total?: number;
  projects?: CodableProject[];
}

interface ProjectOperation {
  id: string;
  created_at?: string | null;
  project_id?: string | null;
  operation_type: 'create' | 'sync' | 'promote' | 'delete' | 'topology-set' | 'build';
  status: 'queued' | 'running' | 'completed' | 'failed';
  phase: string;
  progress: number;
  error?: string | null;
  result?: { ok?: boolean; error?: string } | null;
}

interface SecuritySummary {
  high: number;
  med: number;
  low: number;
  total: number;
}

interface SecurityResultsResp {
  ok?: boolean;
  error?: string;
  scan_incomplete?: boolean;
  warning?: string;
  target_in_scan?: boolean;
  filtered_summary?: SecuritySummary;
}

// Gradient placeholder "thumbnaily" — deterministicky podle hash(slug) % 6,
// aby měl každý projekt stabilní barvu napříč reloady.
const THUMB_GRADIENTS = [
  'from-blue-600 to-indigo-500',
  'from-emerald-600 to-teal-500',
  'from-purple-600 to-fuchsia-500',
  'from-amber-600 to-orange-500',
  'from-rose-600 to-pink-500',
  'from-cyan-600 to-sky-500',
];

function slugHash(slug: string): number {
  let h = 0;
  for (let i = 0; i < slug.length; i++) h = ((h * 31) + slug.charCodeAt(i)) | 0;
  return Math.abs(h);
}

function formatActivity(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('cs-CZ', { day: 'numeric', month: 'numeric', year: 'numeric' });
}

// Plný timestamp do tooltipu (datum + čas) pro „poslední změna".
function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('cs-CZ', {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function ProjectSecurityIcon(
  { slug, label, scope }: { slug: string; label?: string; scope: EffectiveScope },
) {
  const { t } = useTranslation();
  const { data, isLoading } = useQuery({
    queryKey: ['codable-security-results', scope.node ?? 'local', slug],
    queryFn: () =>
      callWidgetTool<SecurityResultsResp>('codable.security_results', { slug }, scope, {
        silent: true,
      }),
    retry: false,
    staleTime: 60_000,
  });
  const summary = data?.filtered_summary;
  const className = isLoading
    ? 'text-gray-400'
    : data?.scan_incomplete
    ? 'text-amber-300'
    : data?.error || !summary || data.target_in_scan === false
    ? 'text-gray-400'
    : summary.high > 0
    ? 'text-red-300'
    : summary.med > 0
    ? 'text-amber-300'
    : summary.total > 0
    ? 'text-blue-300'
    : 'text-emerald-300';
  const titlePrefix = label ? `${label}: ` : '';
  const title = isLoading
    ? t('codable.securityLoading')
    : data?.scan_incomplete
    ? t('codable.securityIncomplete', { detail: data.error ?? data.warning ?? '' })
    : data?.error || !summary || data.target_in_scan === false
    ? t('codable.securityNone')
    : summary.high > 0
    ? t('codable.securityHigh', { n: summary.high })
    : summary.med > 0
    ? t('codable.securityMed', { n: summary.med })
    : summary.total > 0
    ? t('codable.securityLow', { n: summary.total })
    : t('codable.securityClean');
  return (
    <span
      className='inline-flex items-center justify-center rounded-md border border-gray-800 bg-gray-950/80 p-1 shrink-0'
      title={`${titlePrefix}${title}`}
    >
      <Shield size={13} className={className} />
    </span>
  );
}

function ProjectThumbnail(
  { project, gradient, t }: { project: CodableProject; gradient: string; t: TFn },
) {
  const url = project.preview_url ??
    (project.frontend_domain ? `https://${project.frontend_domain}` : null);
  return (
    <div className={`relative h-40 overflow-hidden bg-gradient-to-br ${gradient}`}>
      {url && (
        <iframe
          src={url}
          title={t('codable.previewOf', { name: project.slug })}
          loading='lazy'
          sandbox='allow-scripts allow-same-origin'
          className='absolute left-0 top-0 h-[400%] w-[400%] origin-top-left pointer-events-none bg-white'
          style={{ transform: 'scale(0.25)' }}
        />
      )}
      <div className='absolute inset-0 bg-gradient-to-t from-gray-950/65 via-transparent to-black/15' />
      {!url && (
        <div className='absolute inset-0 flex items-center justify-center'>
          <span className='text-4xl font-black text-white/80 select-none'>
            {(project.name || project.slug).charAt(0).toUpperCase()}
          </span>
        </div>
      )}
    </div>
  );
}

function publicationBadge(
  actual: boolean,
  desired: boolean,
  t: TFn,
): { variant: 'green' | 'gray' | 'yellow'; label: string } {
  if (actual) {
    return {
      variant: 'green',
      label: desired ? t('codable.prodLive') : t('codable.prodTurningOff'),
    };
  }
  if (desired) return { variant: 'yellow', label: t('codable.publishing') };
  return { variant: 'gray', label: t('codable.devOnly') };
}

function UrlLine(
  { label, url, tone, note, security }: {
    label: string;
    url?: string | null;
    tone: 'dev' | 'public' | 'muted';
    note?: string;
    security?: ReactNode;
  },
) {
  const { t } = useTranslation();
  const toneClass = tone === 'public'
    ? 'text-emerald-400 hover:text-emerald-300'
    : tone === 'dev'
    ? 'text-blue-300 hover:text-blue-200'
    : 'text-gray-600';
  return (
    <div className='flex items-center gap-2 min-w-0 text-xs'>
      <span className='w-10 shrink-0 text-[10px] font-semibold uppercase tracking-wide text-gray-600'>
        {label}
      </span>
      {url
        ? (
          <a
            href={url}
            target='_blank'
            rel='noreferrer'
            onClick={(e) => e.stopPropagation()}
            className={`flex min-w-0 items-center gap-1.5 hover:underline ${toneClass}`}
            title={url}
          >
            <Globe size={12} className='shrink-0' />
            <span className='truncate'>{url.replace(/^https?:\/\//, '')}</span>
          </a>
        )
        : <span className='text-gray-600'>{t('codable.notAvailable')}</span>}
      {note && (
        <span className='shrink-0 rounded border border-amber-500/20 bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-medium text-amber-300'>
          {note}
        </span>
      )}
      {security}
    </div>
  );
}

function PublicationLine(
  { url, actual, desired, shared, security }: {
    url?: string | null;
    actual: boolean;
    desired: boolean;
    shared?: boolean;
    security?: ReactNode;
  },
) {
  const { t } = useTranslation();
  if (actual && url) {
    return (
      <UrlLine
        label='PROD'
        url={url}
        tone='public'
        note={shared ? t('codable.sameFeInstance') : undefined}
        security={security}
      />
    );
  }
  const text = desired
    ? t('codable.pubInProgress')
    : shared
    ? t('codable.notPublishedShared')
    : t('codable.notPublished');
  return (
    <div className='flex items-center gap-2 min-w-0 text-xs'>
      <span className='w-10 shrink-0 text-[10px] font-semibold uppercase tracking-wide text-gray-600'>
        PROD
      </span>
      <span className={desired ? 'text-amber-300' : 'text-gray-600'}>{text}</span>
      {desired && security}
    </div>
  );
}

function normalizeUrlForCompare(url?: string | null): string | null {
  if (!url) return null;
  return url.replace(/\/+$/, '').toLowerCase();
}

function topologyBadge(
  p: CodableProject,
  hasDevUrl: boolean,
  hasPublicUrl: boolean,
  t: TFn,
): { variant: 'green' | 'gray' | 'yellow'; label: string; shared?: boolean } {
  const topology = p.topology;
  const mode = topology?.mode ?? null;
  const verified = topology?.verified === true || !!mode;
  if (verified && mode) {
    const label = topology?.label ??
      (mode === 'beta'
        ? 'Beta'
        : mode === 'simple'
        ? 'Simple'
        : mode === 'normal'
        ? 'Normal'
        : mode === 'full_split'
        ? 'Full Split'
        : mode);
    return {
      variant: mode === 'beta' ? 'gray' : mode === 'simple' ? 'yellow' : 'green',
      label,
      shared: topology?.frontend_split === false,
    };
  }

  // Rozdílné DEV/PROD URL neznamenají oddělené FE. Simple režim má jednu FE
  // instanci a dvě adresy, takže bez ověřené topologie nesmíme tvrdit split.
  if (hasDevUrl && !hasPublicUrl) {
    return { variant: 'gray', label: 'Beta' };
  }
  if (hasDevUrl && hasPublicUrl) {
    return { variant: 'yellow', label: t('codable.topologyUnverified') };
  }
  if (hasPublicUrl) {
    return { variant: 'gray', label: t('codable.publicOnly') };
  }
  return { variant: 'gray', label: t('codable.noUrl') };
}

function driftBadge(
  status: ProjectStatus | null | undefined,
  t: TFn,
): { variant: 'green' | 'gray' | 'yellow' | 'red'; label: string; title: string } {
  const states = new Set(status?.relation?.states ?? []);
  const title = status?.relation?.warnings?.[0] ?? t('codable.wsDriftClean');
  if (
    states.has('prod_ahead_git') || states.has('prod_diverged_git')
  ) {
    return { variant: 'red', label: t('codable.driftProd'), title };
  }
  if (states.has('dev_behind_git')) {
    return { variant: 'yellow', label: t('codable.driftGitNewer'), title };
  }
  if (states.has('prod_behind_git')) {
    return { variant: 'yellow', label: t('codable.driftProdBehind'), title };
  }
  if (states.has('dev_dirty')) {
    return { variant: 'yellow', label: t('codable.driftDevDirty'), title };
  }
  if (states.has('prod_preview_waiting')) {
    return { variant: 'yellow', label: t('codable.driftPreviewWaiting'), title };
  }
  if (states.has('git_fetch_warning')) return { variant: 'gray', label: 'Git?', title };
  return { variant: 'green', label: 'sync', title };
}

function shortUrl(url?: string | null, empty = '—'): string {
  if (!url) return empty;
  return url.replace(/^https?:\/\//, '');
}

function BackendAddressLine(
  { label, url, port, tone }: {
    label: string;
    url?: string | null;
    port?: string | null;
    tone: 'local' | 'public' | 'muted';
  },
) {
  const { t } = useTranslation();
  const toneClass = tone === 'public'
    ? 'text-emerald-400 hover:text-emerald-300'
    : tone === 'local'
    ? 'text-sky-300 hover:text-sky-200'
    : 'text-gray-600';
  return (
    <div className='flex items-center gap-2 min-w-0 text-xs'>
      <span className='w-12 shrink-0 text-[10px] font-semibold uppercase tracking-wide text-gray-600'>
        {label}
      </span>
      {url
        ? (
          <span className={`flex min-w-0 items-center gap-1.5 ${toneClass}`} title={url}>
            <Database size={12} className='shrink-0' />
            <span className='truncate'>{shortUrl(url)}</span>
          </span>
        )
        : <span className='text-gray-600'>{t('codable.notAvailable')}</span>}
      {port && (
        <span className='shrink-0 rounded border border-gray-800 bg-gray-950 px-1.5 py-0.5 text-[10px] font-mono text-gray-400'>
          :{port}
        </span>
      )}
    </div>
  );
}

function BackendTopology({ backend }: { backend?: CodableProject['backend'] }) {
  const { t } = useTranslation();
  const runtime = backend?.runtime ?? null;
  const kind = backend?.kind ?? backend?.type ?? runtime?.kind ?? runtime?.module ?? 'none';
  const enabled = backend?.enabled ?? (kind !== 'none');
  if (!enabled || kind === 'none') {
    return (
      <div className='flex items-center justify-between gap-2 pt-1'>
        <span className='text-[10px] font-semibold uppercase tracking-wide text-gray-600'>
          Backend
        </span>
        <Badge variant='gray'>{t('codable.backendNone')}</Badge>
      </div>
    );
  }

  const status = runtime?.status ?? 'unknown';
  const statusVariant = status === 'running' ? 'green' : status === 'unknown' ? 'gray' : 'yellow';
  // Lokalita = kde běží INSTANCE (external/cizí node/lokál). Veřejná URL
  // neznamená jiný backend — je to jen existující veřejná adresa.
  const locality = runtime?.locality === 'external'
    ? t('codable.external')
    : runtime?.locality === 'node'
    ? (runtime?.target_node ?? t('codable.otherNode'))
    : runtime?.locality === 'local'
    ? t('codable.local')
    : runtime?.locality === 'remote'
    ? t('codable.remote')
    : t('codable.unknown');
  const sharedLabel = runtime?.same_instance
    ? t('codable.sharedBe')
    : runtime?.same_instance === false
    ? t('codable.splitBe')
    : t('codable.beLinkUnknown');

  return (
    <details className='group pt-1'>
      <summary className='flex cursor-pointer list-none items-center justify-between gap-2'>
        <span className='text-[10px] font-semibold uppercase tracking-wide text-gray-600'>
          Backend
        </span>
        <div className='flex min-w-0 items-center gap-1'>
          <Badge variant='blue'>{kind}</Badge>
          <Badge variant={statusVariant}>{locality}</Badge>
        </div>
      </summary>
      <div className='mt-1.5 space-y-1.5 border-t border-gray-800/70 pt-1.5'>
        <BackendAddressLine
          label={t('codable.local')}
          url={runtime?.local_url ?? null}
          port={runtime?.port ?? null}
          tone={runtime?.local_url ? 'local' : 'muted'}
        />
        <BackendAddressLine
          label='Public'
          url={runtime?.public_url ?? null}
          port={runtime?.public_port ?? null}
          tone={runtime?.public_url ? 'public' : 'muted'}
        />
        <div className='flex min-w-0 items-center justify-between gap-2 text-[10px]'>
          <span className='truncate text-gray-500' title={runtime?.instance ?? undefined}>
            {runtime?.instance ?? t('codable.instanceUnknown')}
          </span>
          <span className='shrink-0 text-gray-500'>{sharedLabel}</span>
        </div>
      </div>
    </details>
  );
}

function ProjectsLoadingGrid() {
  const { t } = useTranslation();
  return (
    <div className='space-y-3'>
      <div className='flex items-center gap-3 rounded-lg border border-gray-800 bg-gray-900/70 px-4 py-3'>
        <div className='relative flex h-10 w-10 items-center justify-center rounded-lg bg-blue-500/10'>
          <CodableIcon size={18} className='text-blue-300 animate-pulse' />
          <span className='absolute inset-0 rounded-lg border border-blue-500/20 animate-pulse' />
        </div>
        <div className='min-w-0 flex-1'>
          <div className='text-sm font-semibold text-white'>{t('codable.loadingProjects')}</div>
          <div className='text-xs text-gray-500'>
            {t('codable.loadingProjectsHint')}
          </div>
        </div>
      </div>
      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className='overflow-hidden rounded-lg border border-gray-800 bg-gray-900'
          >
            <div className='h-40 bg-gray-800/70 animate-pulse' />
            <div className='space-y-3 p-4'>
              <div className='flex items-center justify-between gap-3'>
                <div className='h-4 w-36 rounded bg-gray-800 animate-pulse' />
                <div className='h-5 w-20 rounded-full bg-gray-800 animate-pulse' />
              </div>
              <div className='h-3 w-28 rounded bg-gray-800 animate-pulse' />
              <div className='space-y-2 pt-1'>
                <div className='h-3 w-full rounded bg-gray-800 animate-pulse' />
                <div className='h-3 w-5/6 rounded bg-gray-800 animate-pulse' />
                <div className='h-3 w-2/3 rounded bg-gray-800 animate-pulse' />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Hlavní seznam projektů jako widget (scope/node z ModulePanel).
 * Page chrome (title + NodePicker + tabs) kreslí ModuleWorkspace.
 */
export function CodableProjectsWidget({ scope }: WidgetRenderProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const nodeKey = scope.node ?? 'local';
  const [deleteConfirm, setDeleteConfirm] = useState<CodableProject | null>(null);
  const [deleteRuntimeData, setDeleteRuntimeData] = useState(false);
  const [activeDeleteOperationId, setActiveDeleteOperationId] = useState<string | null>(null);
  const [handledDeleteOperationId, setHandledDeleteOperationId] = useState<string | null>(null);
  const [cloneSource, setCloneSource] = useState<CodableProject | null>(null);
  const [cloneName, setCloneName] = useState('');
  const [acmImportOpen, setAcmImportOpen] = useState(false);
  const { data, isLoading, error: projectsError } = useQuery({
    queryKey: ['codable-projects', nodeKey],
    queryFn: () => callWidgetTool<ListResp>('codable.project_list', {}, scope),
    staleTime: 30_000,
    retry: false,
  });
  // „Tool not found" na vzdáleném uzlu = modul tam NENÍ nainstalovaný. To je
  // jiná informace než „nejsou projekty" (Filip 2026-09-02) — a admin ho odsud
  // rovnou doinstaluje.
  const moduleMissing = Boolean(
    scope.node && projectsError &&
      /tool not found/i.test((projectsError as Error).message ?? ''),
  );
  const installModule = useMutation({
    mutationFn: () =>
      mcpCall<{ ok?: boolean; error?: string }>('orchestrator.orch_module_install', {
        node: scope.node,
        module: 'codable',
        with_dependencies: true,
      }),
    onSuccess: (r) => {
      if (r?.ok === false) {
        toast.error(r.error ?? t('codable.installFail', 'Instalace modulu selhala'));
        return;
      }
      toast.success(
        t('codable.installStarted', 'Modul se instaluje — uzel se restartuje, chvíli to potrvá'),
      );
      setTimeout(() => void qc.invalidateQueries({ queryKey: ['codable-projects', nodeKey] }), 45_000);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteOperationsQ = useQuery({
    queryKey: ['orchestrator-project-operations', 'mine', nodeKey],
    queryFn: () =>
      callWidgetTool<{ ok: boolean; operations?: ProjectOperation[] }>(
        'orchestrator.project_operation_list',
        { mine: true, limit: 30 },
        scope,
        { silent: true },
      ),
    staleTime: 5_000,
  });
  useEffect(() => {
    if (activeDeleteOperationId) return;
    const operation = pickLiveOperation(deleteOperationsQ.data?.operations, {
      matches: (candidate) => candidate.operation_type === 'delete',
      live: ['queued', 'running', 'failed'],
    });
    if (!operation) return;
    setActiveDeleteOperationId(operation.id);
    const project = data?.projects?.find((candidate) =>
      candidate.orchestrator_project_id === operation.project_id
    );
    if (project) setDeleteConfirm(project);
  }, [activeDeleteOperationId, data?.projects, deleteOperationsQ.data?.operations]);

  const deleteOperationQ = useQuery({
    queryKey: ['orchestrator-project-operation', nodeKey, activeDeleteOperationId],
    queryFn: () =>
      callWidgetTool<{ ok: boolean; operation?: ProjectOperation; error?: string }>(
        'orchestrator.project_operation_get',
        { operation_id: activeDeleteOperationId },
        scope,
        { silent: true },
      ),
    enabled: !!activeDeleteOperationId,
    refetchInterval: (query) =>
      operationPollInterval(
        query.state.data?.operation?.status,
        query.state.dataUpdateCount,
      ),
  });
  const deleteOperation = deleteOperationQ.data?.operation;
  const deleteRunning = deleteOperation?.status === 'queued' ||
    deleteOperation?.status === 'running';
  useEffect(() => {
    if (!deleteOperation || handledDeleteOperationId === deleteOperation.id) return;
    if (deleteOperation.status === 'completed') {
      setHandledDeleteOperationId(deleteOperation.id);
      toast.success(t('codable.deleted'));
      setDeleteConfirm(null);
      setActiveDeleteOperationId(null);
      void qc.invalidateQueries({ queryKey: ['codable-projects', nodeKey] });
    } else if (deleteOperation.status === 'failed') {
      setHandledDeleteOperationId(deleteOperation.id);
      toast.error(deleteOperation.error ?? t('codable.deleteFailed'));
    }
  }, [deleteOperation, handledDeleteOperationId, nodeKey, qc, t]);

  const deleteMut = useMutation({
    mutationFn: (p: CodableProject) => {
      if (!p.orchestrator_project_id) {
        throw new Error(t('codable.noOrchProjectId', 'Projekt nemá orchestrator project ID'));
      }
      return callWidgetTool<{ ok: boolean; operation_id?: string; error?: string }>(
        `${p.orchestrator_project_id}.orchestrator.delete`,
        { keep_data: !deleteRuntimeData },
        scope,
      );
    },
    onSuccess: (r) => {
      if (r.ok && r.operation_id) {
        setHandledDeleteOperationId(null);
        setActiveDeleteOperationId(r.operation_id);
      } else toast.error(r.error ?? t('codable.deleteFailed'));
    },
    onError: (e: unknown) => toast.error(String(e)),
  });

  const retryDeleteMut = useMutation({
    mutationFn: () =>
      callWidgetTool<{ ok: boolean; error?: string }>(
        'orchestrator.project_operation_retry',
        { operation_id: activeDeleteOperationId },
        scope,
      ),
    onSuccess: (result) => {
      if (!result.ok) {
        toast.error(result.error ?? t('codable.deleteFailed'));
        return;
      }
      setHandledDeleteOperationId(null);
      void deleteOperationQ.refetch();
    },
    onError: (error: unknown) => toast.error(String((error as Error).message ?? error)),
  });

  // Klon = project_start s template_url zdrojového repa (template_copy flow —
  // atomic commit kopie, zdroj se nemění, historie se nepřenáší).
  const cloneMut = useMutation({
    mutationFn: async (src: CodableProject) => {
      const detail = await callWidgetTool<{ ok: boolean; gitlab_web_url?: string | null }>(
        'codable.project_get',
        { id: src.id },
        scope,
      );
      if (!detail.gitlab_web_url) {
        throw new Error(t('codable.noGitlabRepoClone'));
      }
      const started = await callWidgetTool<{
        ok: boolean;
        operation_id?: string;
        id?: string;
        error?: string;
      }>('codable.project_start', {
        name: cloneName.trim() || undefined,
        template_url: detail.gitlab_web_url,
        description: src.description ?? t('codable.cloneOf', 'Klon projektu {{name}}', { name: src.name }),
      }, scope);
      if (!started.ok || !started.operation_id) return started;
      for (let attempt = 0; attempt < 600; attempt++) {
        const status = await callWidgetTool<{
          ok: boolean;
          operation?: {
            status: 'queued' | 'running' | 'completed' | 'failed';
            result?: { ok: boolean; id?: string; error?: string };
            error?: string;
          };
          error?: string;
        }>(
          'orchestrator.project_operation_get',
          { operation_id: started.operation_id },
          scope,
          { silent: true },
        );
        if (!status.ok) return { ok: false, error: status.error };
        if (status.operation?.status === 'completed') {
          return status.operation.result ?? { ok: false, error: t('codable.operationNoResult', 'Operace nemá výsledek') };
        }
        if (status.operation?.status === 'failed') {
          return {
            ok: false,
            error: status.operation.error ?? status.operation.result?.error ?? t('codable.cloneFailed'),
          };
        }
        await new Promise((resolve) => setTimeout(resolve, 1_000));
      }
      return { ok: false, error: t('codable.cloneTimeout', 'Vypršel limit čekání na klonování projektu') };
    },
    onSuccess: (r) => {
      if (!r.ok) {
        toast.error(r.error ?? t('codable.cloneFailed'));
        return;
      }
      toast.success(t('codable.cloneDone'));
      setCloneSource(null);
      setCloneName('');
      void qc.invalidateQueries({ queryKey: ['codable-projects', nodeKey] });
      if (r.id) navigate(`/modules/codable/${r.id}`);
    },
    onError: (e: unknown) => toast.error((e as Error).message ?? String(e)),
  });

  // Při změně nodu zruš rozpracované modaly (jiný seznam projektů).
  useEffect(() => {
    setDeleteConfirm(null);
    setActiveDeleteOperationId(null);
    setHandledDeleteOperationId(null);
    setCloneSource(null);
    setAcmImportOpen(false);
  }, [nodeKey]);

  const projects = data?.projects ?? [];
  const [topologyFilter, setTopologyFilter] = useState<
    'all' | 'beta' | 'simple' | 'normal' | 'full_split'
  >('all');
  const [searchQ, setSearchQ] = useState('');

  const filteredProjects = useMemo(() => {
    const q = searchQ.trim().toLowerCase();
    return projects.filter((p) => {
      const mode = (p.topology?.target ?? p.topology?.mode ?? null) as string | null;
      if (topologyFilter !== 'all') {
        if (topologyFilter === 'beta') {
          // beta = explicit beta OR no prod publish (fallback)
          if (mode && mode !== 'beta') return false;
          if (!mode && p.publish?.actual) return false;
        } else if (mode !== topologyFilter) {
          return false;
        }
      }
      if (!q) return true;
      return (
        p.slug.toLowerCase().includes(q) ||
        (p.name ?? '').toLowerCase().includes(q) ||
        (p.orchestrator_project_id ?? '').toLowerCase().includes(q)
      );
    });
  }, [projects, topologyFilter, searchQ]);

  const topologyCounts = useMemo(() => {
    const c: Record<string, number> = { all: projects.length, beta: 0, simple: 0, normal: 0, full_split: 0 };
    for (const p of projects) {
      const mode = (p.topology?.target ?? p.topology?.mode ?? null) as string | null;
      if (mode === 'simple' || mode === 'normal' || mode === 'full_split') c[mode]++;
      else c.beta++;
    }
    return c;
  }, [projects]);

  return (
    <div className='space-y-4 p-4'>
      <div className='flex flex-wrap items-center gap-2'>
        <Link to='/modules/codable/new'>
          <Button variant='primary' icon={<Plus size={15} />}>{t('codable.newProject')}</Button>
        </Link>
        <Button
          variant='secondary'
          icon={<DownloadCloud size={15} />}
          onClick={() => setAcmImportOpen(true)}
          title={t('codable.importFromAcmHint')}
        >
          {t('codable.importFromAcm')}
        </Button>
        <Link to='/modules/codable/new?tab=import'>
          <Button variant='secondary' icon={<DownloadCloud size={15} />}>
            {t('codable.importWizard')}
          </Button>
        </Link>
        {scope.node && (
          <span className='ml-auto text-xs text-gray-500 font-mono truncate max-w-[12rem]' title={scope.node}>
            @{scope.node}
          </span>
        )}
      </div>

      {/* Filtry — varianta + text */}
      <div className='flex flex-wrap items-center gap-2'>
        <input
          value={searchQ}
          onChange={(e) => setSearchQ(e.target.value)}
          placeholder={t('codable.filterProjects', 'Filter projects…')}
          className='bg-gray-950 border border-gray-700 rounded-lg px-3 py-1.5 text-xs text-white w-48 focus:outline-none focus:border-blue-500'
        />
        {(
          [
            ['all', t('codable.filterAll', 'All')],
            ['beta', 'Beta'],
            ['simple', 'Simple'],
            ['normal', 'Normal'],
            ['full_split', 'Full Split'],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type='button'
            onClick={() => setTopologyFilter(id)}
            className={`px-2.5 py-1 rounded-lg text-[11px] font-medium border transition-colors ${
              topologyFilter === id
                ? 'bg-blue-500/20 text-blue-300 border-blue-500/40'
                : 'text-gray-500 border-gray-800 hover:border-gray-700 hover:text-gray-300'
            }`}
          >
            {label}
            <span className='ml-1 text-gray-600'>({topologyCounts[id] ?? 0})</span>
          </button>
        ))}
      </div>

      {acmImportOpen && (
        <AcmImportModal
          onClose={() => setAcmImportOpen(false)}
          onImported={() => {
            setAcmImportOpen(false);
            void qc.invalidateQueries({ queryKey: ['codable-projects', nodeKey] });
          }}
        />
      )}

      {isLoading && <ProjectsLoadingGrid />}

      {!isLoading && moduleMissing && (
        <div className='rounded-2xl border border-amber-500/30 bg-gray-900'>
          <EmptyState
            className='py-16'
            icon={<Puzzle size={28} className='mx-auto text-amber-400' />}
            title={t('codable.moduleMissingTitle', 'Modul Codable na tomto uzlu není nainstalován')}
            description={t(
              'codable.moduleMissingDesc',
              'Uzel je dostupný, ale codable na něm neběží — proto tu nejsou žádné projekty ani ovládání.',
            )}
            action={
              <Button
                variant='primary'
                disabled={installModule.isPending}
                onClick={() => installModule.mutate()}
              >
                {installModule.isPending
                  ? t('codable.installing', 'Instaluji…')
                  : t('codable.installModule', 'Nainstalovat modul na tento uzel')}
              </Button>
            }
          />
        </div>
      )}

      {!isLoading && !moduleMissing && projects.length === 0 && (
        <div className='rounded-2xl border border-gray-800 bg-gray-900'>
          <EmptyState
            className='py-16'
            icon={
              <img
                src='/codable/logo.jpg'
                alt='Codable'
                className='mx-auto w-24 h-24 rounded-2xl shadow-lg'
              />
            }
            title={t('codable.noProjectsTitle')}
            description={t('codable.noProjectsDesc')}
            action={
              <Link to='/modules/codable/new'>
                <Button variant='primary' icon={<Plus size={15} />}>
                  {t('codable.createProject')}
                </Button>
              </Link>
            }
          />
        </div>
      )}

      {!isLoading && projects.length > 0 && filteredProjects.length === 0 && (
        <div className='text-sm text-gray-500 py-8 text-center'>
          {t('codable.filterEmpty', 'No projects match this filter.')}
        </div>
      )}

      {filteredProjects.length > 0 && (
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
          {filteredProjects.map((p) => {
            const gradient = THUMB_GRADIENTS[slugHash(p.slug) % THUMB_GRADIENTS.length];
            // Zdroj pravdy pro veřejnost = frontend_public_enabled (co publish
            // reálně nastaví + backed doménou). Sloupec frontend_public byl řízen
            // deprecovaným PublishControllerem, který ho už nikdy nenastaví na
            // true → staré `enabled !== public` porovnání drželo KAŽDÝ publikovaný
            // projekt navždy v „syncing" (Filip 2026-07-12).
            const previewUrl = p.preview_url ??
              (p.frontend_domain ? `https://${p.frontend_domain}` : null);
            const publicUrl = p.public_url ?? null;
            const hasDevUrl = !!normalizeUrlForCompare(previewUrl);
            const hasPublicUrl = !!normalizeUrlForCompare(publicUrl);
            const topology = topologyBadge(p, hasDevUrl, hasPublicUrl, t);
            const publicActual = p.public_actual ?? p.frontend_public;
            const publicDesired = p.public_desired ?? p.frontend_public_enabled;
            const publicState = publicationBadge(publicActual, publicDesired, t);
            return (
              <div key={p.id} className='relative group'>
                <Link to={`/modules/codable/${p.id}`} className='block'>
                  <div className='rounded-lg border border-gray-800 bg-gray-900 overflow-hidden hover:border-blue-500/40 hover:-translate-y-0.5 transition duration-200'>
                    <ProjectThumbnail project={p} gradient={gradient} t={t} />
                    <div className='p-4'>
                      <div className='flex items-start justify-between gap-2 mb-1'>
                        <h3 className='font-bold text-white group-hover:text-blue-300 truncate'>
                          {p.name}
                        </h3>
                        <Badge
                          variant={publicState.variant}
                          dot={publicActual || publicDesired}
                          className='flex-shrink-0'
                        >
                          {publicState.label}
                        </Badge>
                      </div>
                      <div className='text-xs text-gray-500 font-mono mb-1.5 truncate'>
                        {p.slug}
                      </div>
                      {/* id + changeset (Filip 2026-07-12: karta rovnou ukáže changeset, čas a id) */}
                      <div className='flex items-center gap-2 mb-2 text-[10px] font-mono text-gray-600'>
                        {(() => {
                          const shown = visibleProjectId(p.orchestrator_project_id, p.id);
                          return shown
                            ? (
                              <span
                                className='inline-flex select-all items-center gap-1 shrink-0 text-gray-400'
                                title={t('codable.prjIdTitle', 'Project ID: {{id}}', { id: shown })}
                              >
                                <Hash size={10} />
                                {shown}
                              </span>
                            )
                            : null;
                        })()}
                        {p.last_commit_sha && (
                          <span
                            className='inline-flex items-center gap-1 shrink-0'
                            title={t('codable.lastCommitTitle', 'Poslední commit: {{sha}}', { sha: p.last_commit_sha })}
                          >
                            <GitCommit size={10} />
                            {p.last_commit_sha.slice(0, 7)}
                          </span>
                        )}
                      </div>
                      {/* Zdroj + backend stack (Filip 2026-07-12) */}
                      <div className='flex flex-wrap items-center gap-1 mb-2'>
                        {(() => {
                          const s = sourceBadge(p, t);
                          return <Badge variant={s.variant}>{s.label}</Badge>;
                        })()}
                        {p.origin?.mode === 'mirror' && (
                          <Badge variant='yellow'>{t('codable.mirror', 'mirror')}</Badge>
                        )}
                        {p.origin?.stale && (
                          <Badge variant='red'>{t('codable.staleSource', '⚠ zdroj offline')}</Badge>
                        )}
                        {(() => {
                          const be = backendBadge(p, t);
                          return be ? <Badge variant={be.variant}>{be.label}</Badge> : null;
                        })()}
                        {(() => {
                          const d = driftBadge(p.project_status, t);
                          return (
                            <span title={d.title}>
                              <Badge variant={d.variant}>
                                {d.variant === 'red' || d.variant === 'yellow'
                                  ? <AlertTriangle size={10} className='mr-1' />
                                  : null}
                                {d.label}
                              </Badge>
                            </span>
                          );
                        })()}
                      </div>
                      {p.description && (
                        <p className='text-sm text-gray-400 line-clamp-2 mb-2'>{p.description}</p>
                      )}
                      <div className='space-y-1.5'>
                        <div className='flex items-center justify-between gap-2'>
                          <span className='text-[10px] font-semibold uppercase tracking-wide text-gray-600'>
                            {t('codable.instanceLabel', 'Instance')}
                          </span>
                          <Badge variant={topology.variant}>{topology.label}</Badge>
                        </div>
                        <UrlLine
                          label='Dev'
                          url={previewUrl}
                          tone='dev'
                          security={previewUrl
                            ? <ProjectSecurityIcon slug={p.slug} label='Dev URL' scope={scope} />
                            : undefined}
                        />
                        <PublicationLine
                          url={publicUrl}
                          actual={publicActual}
                          desired={publicDesired}
                          shared={topology.shared}
                          security={publicUrl
                            ? <ProjectSecurityIcon slug={p.slug} label='PROD URL' scope={scope} />
                            : undefined}
                        />
                        <BackendTopology backend={p.backend} />
                      </div>
                      <div className='mt-2 flex items-center justify-end gap-2 text-xs'>
                        <span
                          className='inline-flex items-center gap-1 text-gray-600 flex-shrink-0'
                          title={t('codable.lastChangeTitle', 'Poslední změna: {{when}}', { when: formatDateTime(p.updated_at ?? p.created_at) })}
                        >
                          <Clock size={10} />
                          {formatActivity(p.updated_at ?? p.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
                {/* Hover akce — vpravo nahoře nad thumbnailem */}
                <div className='absolute top-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity'>
                  {p.frontend_domain && (
                    <a
                      href={previewUrl ?? `https://${p.frontend_domain}`}
                      target='_blank'
                      rel='noreferrer'
                      onClick={(e) => e.stopPropagation()}
                      className='p-1.5 rounded-lg bg-black/30 hover:bg-black/50 text-white/80 hover:text-white backdrop-blur-sm'
                      title={t('codable.openPreview')}
                    >
                      <ExternalLink size={14} />
                    </a>
                  )}
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      setCloneName('');
                      setCloneSource(p);
                    }}
                    className='p-1.5 rounded-lg bg-black/30 hover:bg-black/50 text-white/80 hover:text-white backdrop-blur-sm'
                    title={t('codable.klonovatProjekt', 'Klonovat projekt')}
                  >
                    <Copy size={14} />
                  </button>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      setDeleteRuntimeData(false);
                      setActiveDeleteOperationId(null);
                      setHandledDeleteOperationId(null);
                      setDeleteConfirm(p);
                    }}
                    className='p-1.5 rounded-lg bg-black/30 hover:bg-red-500/40 text-red-300 hover:text-red-200 backdrop-blur-sm'
                    title={t('codable.smazatProjekt', 'Smazat projekt')}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Klon projektu — nové repo z template_copy + čerstvý stack */}
      {cloneSource && (
        <Modal
          open
          onClose={() => setCloneSource(null)}
          title={`${t('codable.cloneModalTitle')}: ${cloneSource.name}`}
          footer={
            <>
              <Button
                variant='secondary'
                onClick={() => setCloneSource(null)}
                disabled={cloneMut.isPending}
              >
                {t('codable.cancel')}
              </Button>
              <Button
                variant='primary'
                icon={<Copy size={14} />}
                loading={cloneMut.isPending}
                onClick={() => cloneMut.mutate(cloneSource)}
              >
                {cloneMut.isPending ? t('codable.cloning') : t('codable.clone')}
              </Button>
            </>
          }
        >
          <div className='space-y-3'>
            <p className='text-sm text-gray-400'>
              {t('codable.cloneModalHint')}
            </p>
            <div>
              <Label>{t('codable.settingsName')} ({t('codable.slugOptional')})</Label>
              <Input
                size='sm'
                className='mt-1 font-mono'
                value={cloneName}
                onChange={(e) =>
                  setCloneName(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                placeholder={`${cloneSource.slug}-copy`}
              />
            </div>
          </div>
        </Modal>
      )}

      {deleteConfirm && (
        <div className='fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4'>
          <div className='bg-gray-900 border border-gray-800 rounded-2xl p-6 max-w-md w-full space-y-4'>
            <h3 className='font-bold text-white text-lg'>{t('codable.deleteConfirm')}</h3>
            <p className='text-sm text-gray-400'>
              {t('codable.deleteModalHint')}{' '}
              <span className='font-mono text-xs'>{deleteConfirm.slug}</span>
            </p>
            <label className='flex items-start gap-2 rounded-lg border border-gray-800 bg-gray-950 p-3 text-xs text-gray-400'>
              <input
                type='checkbox'
                className='mt-0.5 accent-red-500'
                checked={deleteRuntimeData}
                disabled={deleteRunning}
                onChange={(event) => setDeleteRuntimeData(event.target.checked)}
              />
              <span>
                {t(
                  'codable.deleteRuntimeData',
                  'Po vytvoření obnovitelné zálohy odstranit také lokální runtime data. Bez zaškrtnutí zůstanou data na nodu; GitLab repo se nemaže nikdy.',
                )}
              </span>
            </label>
            {deleteOperation && (
              <div
                className={`space-y-2 rounded-lg border p-3 text-xs ${
                  deleteOperation.status === 'failed'
                    ? 'border-red-500/40 bg-red-500/5 text-red-200'
                    : 'border-blue-500/30 bg-blue-500/5 text-blue-200'
                }`}
              >
                <div className='flex items-center justify-between gap-2'>
                  <span>{deleteOperation.phase}</span>
                  <span className='font-mono'>{deleteOperation.progress}%</span>
                </div>
                <div className='h-1.5 overflow-hidden rounded-full bg-gray-900'>
                  <div
                    className={deleteOperation.status === 'failed'
                      ? 'h-full bg-red-500'
                      : 'h-full bg-blue-500'}
                    style={{ width: `${Math.max(0, Math.min(100, deleteOperation.progress))}%` }}
                  />
                </div>
                {deleteOperation.error && <div>{deleteOperation.error}</div>}
                {deleteOperation.status === 'failed' && (
                  <Button
                    size='sm'
                    variant='secondary'
                    loading={retryDeleteMut.isPending}
                    onClick={() => retryDeleteMut.mutate()}
                  >
                    {t('codable.zkusitZnovu', 'Zkusit znovu')}
                  </Button>
                )}
              </div>
            )}
            <div className='flex justify-end gap-2'>
              <Button
                variant='secondary'
                onClick={() => setDeleteConfirm(null)}
                disabled={deleteMut.isPending || deleteRunning}
              >
                {t('codable.cancel')}
              </Button>
              <Button
                variant='danger'
                onClick={() => deleteMut.mutate(deleteConfirm)}
                loading={deleteMut.isPending || deleteRunning}
                disabled={deleteRunning || deleteOperation?.status === 'failed'}
              >
                {t('codable.delete')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** @deprecated Prefer ModuleWorkspace + codable.projects widget. */
export default function CodableList() {
  return <CodableProjectsWidget scope={{ node: null, identity: null }} format='lg' viewport='desktop' data={undefined} />;
}
